import { useCallback, useRef, useState } from 'react';

import type { WorkflowEdge, WorkflowNode } from 'model/xyflowMapping';

/**
 * A snapshot of selected graph elements as they were at copy/cut time.
 * Callers pass in whatever subset of the live graph they consider
 * cuttable / copyable — the hook does no filtering of its own.
 */
export interface ClipboardSelection {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

export interface UseWorkflowClipboardResult {
  copy: (selection: ClipboardSelection) => void;
  cut: (selection: ClipboardSelection) => void;
  paste: () => ClipboardSelection | null;
  canPaste: boolean;
  clear: () => void;
}

/**
 * Pure node/edge clipboard. Stores a deep-ish snapshot of the last
 * copy/cut operation in a ref so paste can be invoked any number of
 * times. `canPaste` is a `useState` flag so the toolbar can disable
 * Paste until there's something to paste.
 *
 * Policy (e.g. "you can't cut the initial/output anchors") lives in the
 * caller — this hook is a dumb storage layer. The caller computes the
 * cuttable subset, calls `cut(subset)`, then performs the graph-side
 * removal itself.
 */
export const useWorkflowClipboard = (): UseWorkflowClipboardResult => {
  const clipboardRef = useRef<ClipboardSelection | null>(null);
  const [canPaste, setCanPaste] = useState(false);

  const snapshot = useCallback(
    (selection: ClipboardSelection): ClipboardSelection => ({
      nodes: selection.nodes.map((n) => ({ ...n, data: { ...n.data } })),
      edges: selection.edges.map((e) => ({ ...e, data: e.data ? { ...e.data } : e.data })),
    }),
    [],
  );

  const copy = useCallback(
    (selection: ClipboardSelection) => {
      if (selection.nodes.length === 0) return;
      clipboardRef.current = snapshot(selection);
      setCanPaste(true);
    },
    [snapshot],
  );

  const cut = useCallback(
    (selection: ClipboardSelection) => {
      if (selection.nodes.length === 0) return;
      clipboardRef.current = snapshot(selection);
      setCanPaste(true);
    },
    [snapshot],
  );

  const paste = useCallback((): ClipboardSelection | null => {
    if (!clipboardRef.current) return null;
    // Return a fresh deep-ish copy so consumers can mutate freely without
    // touching the clipboard's stored snapshot.
    return snapshot(clipboardRef.current);
  }, [snapshot]);

  const clear = useCallback(() => {
    clipboardRef.current = null;
    setCanPaste(false);
  }, []);

  return { copy, cut, paste, canPaste, clear };
};
